<template>
  <div>
    <b-row>
      <b-col>
        <b-card title="Statut du certificat Spoticar">
          <div v-if="loadingStatus" class="text-center py-3">
            <b-spinner small /> Chargement...
          </div>
          <div v-else-if="status">
            <b-row class="mb-2">
              <b-col md="4" class="font-weight-bold">État</b-col>
              <b-col>
                <b-badge :variant="badgeVariant">{{ statusLabel }}</b-badge>
                <span v-if="status.days_remaining !== null" class="ml-1">
                  ({{ status.days_remaining }} jour(s) restants)
                </span>
              </b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col md="4" class="font-weight-bold">Sujet (CN)</b-col>
              <b-col>{{ status.subject || '—' }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col md="4" class="font-weight-bold">Émetteur</b-col>
              <b-col>{{ status.issuer || '—' }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col md="4" class="font-weight-bold">Valide à partir du</b-col>
              <b-col>{{ formatDate(status.valid_from) }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col md="4" class="font-weight-bold">Valide jusqu'au</b-col>
              <b-col>{{ formatDate(status.valid_to) }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col md="4" class="font-weight-bold">Fichier .cer</b-col>
              <b-col>{{ status.cer_path }} ({{ status.cer_exists ? 'présent' : 'absent' }})</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col md="4" class="font-weight-bold">Fichier .pk</b-col>
              <b-col>{{ status.pk_path }} ({{ status.pk_present ? 'présent' : 'absent' }})</b-col>
            </b-row>
            <b-row v-if="status.last_modified" class="mb-2">
              <b-col md="4" class="font-weight-bold">Dernière modification</b-col>
              <b-col>{{ formatDate(status.last_modified) }}</b-col>
            </b-row>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-card title="Renouveler le certificat">
          <b-alert variant="warning" show class="mb-2">
            <div class="alert-body">
              Le remplacement écrase les fichiers actuels. Une copie horodatée est conservée
              dans <code>certificats/backups/</code>.
            </div>
          </b-alert>

          <b-form @submit.prevent="confirmUpload">
            <b-form-group label="Fichier .cer (certificat)" label-cols-md="3">
              <b-form-file
                v-model="files.cer"
                :state="files.cer ? true : null"
                accept=".cer,.crt,.pem"
                placeholder="Sélectionner le nouveau fichier .cer"
                drop-placeholder="Déposer le fichier ici..."
              />
            </b-form-group>

            <b-form-group label="Fichier .pk (clé privée)" label-cols-md="3">
              <b-form-file
                v-model="files.pk"
                :state="files.pk ? true : null"
                accept=".pk,.key,.pem"
                placeholder="Sélectionner le nouveau fichier .pk"
                drop-placeholder="Déposer le fichier ici..."
              />
            </b-form-group>

            <div class="text-right">
              <b-button
                type="submit"
                variant="primary"
                :disabled="!files.cer || !files.pk || uploading"
              >
                <b-spinner v-if="uploading" small class="mr-1" />
                Renouveler le certificat
              </b-button>
            </div>
          </b-form>
        </b-card>
      </b-col>
    </b-row>

    <b-modal
      v-model="showConfirm"
      title="Confirmation"
      ok-title="Confirmer"
      cancel-title="Annuler"
      ok-variant="primary"
      @ok="upload"
    >
      Le certificat Spoticar actuel sera sauvegardé puis remplacé. Confirmer&nbsp;?
    </b-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BForm, BFormGroup, BFormFile, BButton, BAlert, BBadge, BSpinner, BModal,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from '@/libs/axios'

export default {
  components: {
    BRow, BCol, BCard, BForm, BFormGroup, BFormFile, BButton, BAlert, BBadge, BSpinner, BModal,
  },
  data() {
    return {
      status: null,
      loadingStatus: false,
      files: { cer: null, pk: null },
      uploading: false,
      showConfirm: false,
    }
  },
  computed: {
    badgeVariant() {
      switch (this.status && this.status.status) {
        case 'valid': return 'success'
        case 'expiring_soon': return 'warning'
        case 'expired':
        case 'invalid':
        case 'missing':
        default: return 'danger'
      }
    },
    statusLabel() {
      switch (this.status && this.status.status) {
        case 'valid': return 'Valide'
        case 'expiring_soon': return 'Expire bientôt'
        case 'expired': return 'Expiré'
        case 'invalid': return 'Invalide'
        case 'missing': return 'Absent'
        default: return '—'
      }
    },
  },
  created() {
    this.fetchStatus()
  },
  methods: {
    fetchStatus() {
      this.loadingStatus = true
      axios.get(process.env.VUE_APP_API_URL + 'api/spoticar/certificat/status')
        .then(({ data }) => { this.status = data })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Erreur',
              text: 'Impossible de charger le statut du certificat',
              variant: 'danger',
            },
          })
        })
        .finally(() => { this.loadingStatus = false })
    },
    confirmUpload() {
      this.showConfirm = true
    },
    upload() {
      if (this.uploading) return
      const formData = new FormData()
      formData.append('cer', this.files.cer)
      formData.append('pk', this.files.pk)
      this.uploading = true
      axios.post(process.env.VUE_APP_API_URL + 'api/spoticar/certificat/upload', formData)
        .then(({ data }) => {
          this.status = data
          this.files = { cer: null, pk: null }
        })
        .finally(() => {
          this.uploading = false
        })
    },
    formatDate(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return iso
      return d.toLocaleDateString('fr-FR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      })
    },
  },
}
</script>
